import React, { useRef, useEffect } from 'react';
import type { BackgroundId, SkinBackground } from '@/lib/skins';
import { SKINS } from '@/lib/skins';

/**
 * Animated WebGL background, one fragment shader per mode, coloured by the
 * active skin. Pauses when the tab is hidden, renders a single still frame
 * under prefers-reduced-motion (or motion = 0) and falls back to CSS when
 * WebGL is unavailable.
 *
 *   nebula — carbon dust + twinkling stars + faint brand plumes (Noir)
 *   aurora — slow ribbons of brand and accent light (Studio Night)
 *   liquid — soft moving colour fields, for light skins (Studio, Blush)
 *   grid   — synthwave perspective grid with horizon glow (Arcade)
 *   silk   — drifting topographic contour lines (Atelier)
 *   blobs  — morphing organic blobs in two colours (color / color2, density)
 *   galaxy — slow spiral galaxy with a bright core
 *   bokeh  — out-of-focus lights drifting upwards (density)
 *   waves  — layered waves with glowing crests
 *   plain  — flat ground with a soft gradient, no animation
 *
 * With `interactive`, the background follows the finger / cursor: a lens pull
 * and a soft glow where it touches, and a ripple on every tap.
 */
interface ShaderBGProps {
  intensity?: number;
  style?: React.CSSProperties;
  mode?: BackgroundId;
  palette?: SkinBackground;
  /** Speed multiplier; 0 renders a still frame */
  motion?: number;
  /** Amount of shapes for blobs and bokeh (0.25–2) */
  density?: number;
  /** React to touch / cursor (lens, glow and tap ripples) */
  interactive?: boolean;
}

const HEADER = `
  precision highp float;
  uniform vec2 u_res;
  uniform float u_time;
  uniform float u_intensity;
  uniform vec3 u_base;
  uniform vec3 u_mid;
  uniform vec3 u_glow;
  uniform vec3 u_accent;
  uniform float u_glowAmt;
  uniform float u_density;
  uniform float u_light;
  // Finger / cursor in centeredUv space: xy position, z strength (0–1)
  uniform vec3 u_pointer;
  // Last tap: xy position, z seconds since it happened
  uniform vec3 u_tap;

  vec3 mod289(vec3 x){return x-floor(x*(1.0/289.0))*289.0;}
  vec2 mod289(vec2 x){return x-floor(x*(1.0/289.0))*289.0;}
  vec3 permute(vec3 x){return mod289(((x*34.0)+1.0)*x);}
  float snoise(vec2 v){
    const vec4 C = vec4(0.211324865405187,0.366025403784439,-0.577350269189626,0.024390243902439);
    vec2 i  = floor(v + dot(v, C.yy) );
    vec2 x0 = v -   i + dot(i, C.xx);
    vec2 i1 = (x0.x>x0.y) ? vec2(1.0,0.0) : vec2(0.0,1.0);
    vec4 x12 = x0.xyxy + C.xxzz;
    x12.xy -= i1;
    i = mod289(i);
    vec3 p = permute( permute( i.y + vec3(0.0, i1.y, 1.0 ))+ i.x + vec3(0.0, i1.x, 1.0 ));
    vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);
    m = m*m; m = m*m;
    vec3 x = 2.0 * fract(p * C.www) - 1.0;
    vec3 h = abs(x) - 0.5;
    vec3 ox = floor(x + 0.5);
    vec3 a0 = x - ox;
    m *= 1.79284291400159 - 0.85373472095314 * ( a0*a0 + h*h );
    vec3 g;
    g.x  = a0.x  * x0.x  + h.x  * x0.y;
    g.yz = a0.yz * x12.xz + h.yz * x12.yw;
    return 130.0 * dot(m, g);
  }
  float hash(vec2 p) {
    p = fract(p * vec2(123.34, 456.21));
    p += dot(p, p + 45.32);
    return fract(p.x * p.y);
  }
  float stars(vec2 uv, float t) {
    vec2 gridId = floor(uv * 32.0);
    vec2 gridUv = fract(uv * 32.0) - 0.5;
    float rand1 = hash(gridId);
    float rand2 = hash(gridId + 7.23);
    float starMask = step(0.70, rand1);
    vec2 starPos = vec2(rand1 - 0.5, rand2 - 0.5) * 0.7;
    float dist = length(gridUv - starPos);
    float twinkle = 0.25 + 0.75 * sin(t * (1.0 + rand1 * 4.0) + rand2 * 6.28);
    float size = 0.005 + rand2 * 0.010;
    return smoothstep(size, 0.0, dist) * twinkle * starMask;
  }
  float grain() { return fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453) - 0.5; }
  vec2 rawUv() { return (gl_FragCoord.xy - 0.5*u_res.xy) / min(u_res.x, u_res.y); }
  // Every mode samples through here, so the touch lens and the tap ripple bend all of them
  vec2 centeredUv() {
    vec2 uv = rawUv();
    vec2 d = uv - u_pointer.xy;
    uv -= d * exp(-dot(d, d) * 18.0) * u_pointer.z * 0.35;
    vec2 dt = uv - u_tap.xy;
    float dist = length(dt);
    float front = dist - u_tap.z * 0.9;
    float wave = sin(front * 38.0) * exp(-u_tap.z * 2.2) * exp(-abs(front) * 10.0);
    uv += dt / max(dist, 1e-4) * wave * 0.025;
    return uv;
  }
  vec3 touchGlow() {
    vec2 uv = rawUv();
    vec2 d = uv - u_pointer.xy;
    float g = exp(-dot(d, d) * 30.0) * u_pointer.z;
    float ring = exp(-abs(length(uv - u_tap.xy) - u_tap.z * 0.9) * 40.0) * exp(-u_tap.z * 2.5);
    return (u_glow * g * 0.18 + mix(u_glow, u_accent, 0.5) * ring * 0.22) * mix(1.0, 0.6, u_light);
  }
`;

const MODES: Record<BackgroundId, string> = {
  // The original Noir universe, tinted by the skin; brand plumes scaled by u_glowAmt.
  nebula: `
    void main() {
      vec2 uv = centeredUv();
      float t = u_time * 0.06;
      float n1 = snoise(uv*0.8 + vec2(t*0.4, -t*0.2));
      float n2 = snoise(uv*1.6 - vec2(-t*0.3, t*0.5));
      float n3 = snoise(uv*0.4 + vec2(t*0.1, t*0.1));
      float field = n1*0.50 + n2*0.32 + n3*0.18;
      float vignette = smoothstep(1.3, 0.25, length(uv));
      float plume = smoothstep(0.05, 0.95, field*0.5 + 0.5);
      vec3 col = u_base;
      col = mix(col, mix(u_base, u_mid, 0.45), plume * 0.75);
      col = mix(col, u_mid, smoothstep(0.35, 0.82, plume) * 0.60);
      col = mix(col, u_mid * 1.7, smoothstep(0.65, 0.96, plume) * 0.35 * u_intensity);
      float ember = smoothstep(0.55, 1.0, plume) * smoothstep(0.2, 0.9, snoise(uv*0.6 - vec2(t*0.2, t*0.15))*0.5+0.5);
      col += u_glow * ember * 0.22 * u_glowAmt * u_intensity;
      float sf = stars(uv, u_time) + stars(uv*1.7 + vec2(15.23, 83.17), u_time*0.8) + stars(uv*2.4 - vec2(42.91, 19.34), u_time*0.65)*0.65;
      col += vec3(sf) * vignette * 0.9;
      col += u_base * 2.0 * vignette * (0.5 + 0.5*sin(u_time*0.5));
      col += grain() * 0.016;
      gl_FragColor = vec4(col, 1.0);
    }`,

  aurora: `
    void main() {
      vec2 uv = centeredUv();
      float t = u_time * 0.08;
      vec3 col = u_base;
      for (int i = 0; i < 3; i++) {
        float fi = float(i);
        float wave = snoise(vec2(uv.x*0.9 + t*(0.6 + fi*0.15) + fi*3.1, t*0.5 + fi)) * 0.28;
        float y = uv.y - (0.18 - fi*0.17) - wave;
        float band = exp(-y*y*(26.0 + fi*14.0));
        float shimmer = 0.65 + 0.35*snoise(vec2(uv.x*3.0 - t*1.4, fi*7.0 + t));
        vec3 tint = mix(u_glow, u_accent, fi*0.5);
        col += tint * band * shimmer * (0.32 - fi*0.07) * u_glowAmt * u_intensity;
      }
      col = mix(col, u_mid, 0.25 * smoothstep(0.2, -0.9, uv.y));
      float sf = stars(uv*1.2, u_time*0.7);
      col += vec3(sf) * 0.35 * smoothstep(-0.1, 0.6, uv.y);
      col *= smoothstep(1.5, 0.3, length(uv*vec2(0.8, 1.0)));
      col += grain() * 0.014;
      gl_FragColor = vec4(col, 1.0);
    }`,

  liquid: `
    void main() {
      vec2 uv = centeredUv();
      float t = u_time * 0.05;
      vec2 w = uv + 0.35*vec2(snoise(uv*0.7 + t), snoise(uv*0.7 - t + 4.0));
      float a = snoise(w*0.9 + vec2(t*0.6, -t*0.3))*0.5 + 0.5;
      float b = snoise(w*1.3 - vec2(-t*0.4, t*0.5) + 9.0)*0.5 + 0.5;
      float c = snoise(w*0.5 + vec2(t*0.2, t*0.25) + 3.0)*0.5 + 0.5;
      vec3 col = u_base;
      col = mix(col, u_mid,    smoothstep(0.45, 0.95, a) * 0.85 * u_glowAmt);
      col = mix(col, u_glow,   smoothstep(0.55, 1.00, b) * 0.55 * u_glowAmt);
      col = mix(col, u_accent, smoothstep(0.50, 1.00, c) * 0.60 * u_glowAmt);
      col = mix(u_base, col, 0.75 * clamp(u_intensity, 0.0, 1.4));
      col += grain() * 0.012;
      gl_FragColor = vec4(col, 1.0);
    }`,

  grid: `
    void main() {
      vec2 uv = centeredUv();
      float t = u_time * 0.35;
      float horizon = -0.05;
      vec3 col = u_base;
      // sky: accent haze + scanlines
      float sky = smoothstep(horizon, 0.9, uv.y);
      float haze = snoise(vec2(uv.x*1.2 + t*0.08, uv.y*1.5 - t*0.05))*0.5 + 0.5;
      col = mix(col, u_mid, (1.0 - sky) * 0.6);
      col += u_accent * haze * 0.07 * (1.0 - sky) * u_glowAmt;
      col += u_glow * exp(-abs(uv.y - horizon)*22.0) * 0.18 * u_glowAmt * u_intensity;
      col += vec3(stars(uv*1.4 + vec2(0.0, t*0.02), u_time)) * 0.4 * sky;
      // floor: perspective grid
      if (uv.y < horizon) {
        float z = 0.35 / (horizon - uv.y);
        vec2 g = vec2(uv.x * z * 1.6, z + t);
        vec2 f = abs(fract(g) - 0.5);
        // anti-aliased 1px-ish lines whatever the depth
        vec2 fw = fwidth(g);
        vec2 l2 = 1.0 - smoothstep(vec2(0.0), fw * 1.5, 0.5 - f);
        float line = max(l2.x, l2.y);
        float fade = smoothstep(7.0, 0.8, z) * smoothstep(0.0, 0.25, horizon - uv.y + 0.02);
        col += u_glow * line * fade * 0.28 * u_glowAmt * u_intensity;
      }
      col *= 0.96 + 0.04*sin(gl_FragCoord.y * 1.6);
      col += grain() * 0.016;
      gl_FragColor = vec4(col, 1.0);
    }`,

  silk: `
    void main() {
      vec2 uv = centeredUv();
      float t = u_time * 0.035;
      float field = snoise(uv*0.9 + vec2(t, -t*0.6))*0.6 + snoise(uv*1.9 - vec2(t*0.7, t))*0.3 + snoise(uv*3.7 + t*0.4)*0.1;
      float k = field * 7.0;
      float d = abs(fract(k) - 0.5);
      float lw = fwidth(k) * 1.2;
      float line = smoothstep(lw, 0.0, d - 0.02);
      float mk = field * 1.75;
      float major = smoothstep(fwidth(mk) * 1.5, 0.0, abs(fract(mk) - 0.5) - 0.004);
      vec3 col = u_base;
      col = mix(col, u_mid, smoothstep(-0.4, 0.8, field) * 0.35 * u_glowAmt);
      col = mix(col, u_glow, line * 0.06 * u_intensity);
      col = mix(col, u_accent, major * 0.10 * u_intensity);
      col += grain() * 0.010;
      gl_FragColor = vec4(col, 1.0);
    }`,
  blobs: `
    void main() {
      vec2 uv = centeredUv();
      float t = u_time * 0.12;
      float count = clamp(floor(3.0 + u_density * 3.0), 2.0, 9.0);
      vec3 acc = vec3(0.0);
      float wsum = 0.0;
      for (int i = 0; i < 9; i++) {
        float fi = float(i);
        if (fi >= count) break;
        float k = fi / max(count - 1.0, 1.0);
        vec2 c = vec2(
          sin(t * (0.7 + 0.13*fi) + fi*2.1) * 0.55,
          cos(t * (0.5 + 0.11*fi) + fi*1.3) * 0.65
        );
        // organic edge: the radius breathes and the outline wobbles with noise
        vec2 d = uv - c;
        float ang = atan(d.y, d.x);
        float wobble = 0.12 * snoise(vec2(ang * 1.5 + fi*3.0, t*1.4 + fi));
        float r = (0.32 + 0.10*sin(t*1.7 + fi*4.0)) * (1.0 + wobble) / sqrt(max(u_density, 0.25));
        float w = exp(-dot(d, d) / (r*r));
        acc += mix(u_glow, u_accent, k) * w;
        wsum += w;
      }
      vec3 blob = acc / max(wsum, 1e-4);
      float cover = smoothstep(0.05, 1.1, wsum);
      // light skins keep the blobs pastel so dark text stays legible
      float amount = mix(0.95, 0.45, u_light) * u_glowAmt * clamp(u_intensity, 0.0, 1.4);
      vec3 col = mix(u_base, blob, cover * amount);
      col += grain() * 0.012;
      gl_FragColor = vec4(col, 1.0);
    }`,

  // Spiral arms around a bright core; stars fade out on light skins.
  galaxy: `
    void main() {
      vec2 uv = centeredUv();
      float t = u_time * 0.03;
      float r = length(uv);
      float a = atan(uv.y, uv.x);
      float spiral = sin(a * 2.0 - log(r + 0.001) * 4.0 + t * 3.0) * 0.5 + 0.5;
      float n = snoise(uv * 2.5 + vec2(t, -t)) * 0.5 + 0.5;
      float arm = smoothstep(0.2, 1.0, spiral) * (1.0 - smoothstep(0.05, 1.2, r));
      vec3 col = u_base;
      col = mix(col, u_mid, (1.0 - smoothstep(0.0, 1.3, r)) * 0.6);
      col += mix(u_glow, u_accent, n) * arm * n * 0.35 * u_glowAmt * u_intensity;
      col += u_glow * exp(-r * r * 18.0) * 0.25 * u_intensity;
      float sf = stars(uv * 1.1, u_time) + stars(uv * 2.0 + vec2(3.1, 7.7), u_time * 0.7) * 0.6;
      col += vec3(sf) * 0.7 * (1.0 - u_light * 0.8);
      col += grain() * 0.014;
      gl_FragColor = vec4(col, 1.0);
    }`,

  // Out-of-focus discs drifting upwards; density sets how many.
  bokeh: `
    void main() {
      vec2 uv = centeredUv();
      float t = u_time * 0.05;
      vec3 col = mix(u_base, u_mid, 0.35);
      float count = clamp(floor(8.0 + u_density * 8.0), 4.0, 24.0);
      for (int i = 0; i < 24; i++) {
        float fi = float(i);
        if (fi >= count) break;
        float h1 = hash(vec2(fi, 1.3));
        float h2 = hash(vec2(fi, 7.1));
        float h3 = hash(vec2(fi, 3.7));
        vec2 c = vec2((h1 - 0.5) * 1.8 + sin(t * (0.5 + h3) + fi) * 0.15, mod(h2 * 2.4 + t * (0.2 + h3 * 0.3), 2.4) - 1.2);
        float r = 0.05 + h3 * 0.12;
        float d = length(uv - c);
        float disc = 1.0 - smoothstep(r * 0.75, r, d);
        float ring = (1.0 - smoothstep(0.0, r * 0.08, abs(d - r * 0.9))) * 0.4;
        vec3 tint = mix(u_glow, u_accent, h1);
        col = mix(col, tint, clamp((disc * 0.22 + ring * 0.14) * u_glowAmt * u_intensity * mix(1.0, 0.7, u_light), 0.0, 1.0));
      }
      col = mix(col, col * (1.0 - smoothstep(0.3, 1.6, length(uv))), 1.0 - u_light);
      col += grain() * 0.012;
      gl_FragColor = vec4(col, 1.0);
    }`,

  // Five layered waves across the lower half, each crest lit by the brand colours.
  waves: `
    void main() {
      vec2 uv = centeredUv();
      float t = u_time * 0.25;
      vec3 col = u_base;
      col = mix(col, u_mid, (1.0 - smoothstep(-0.8, 0.8, uv.y)) * 0.5);
      for (int i = 0; i < 5; i++) {
        float fi = float(i);
        float y = -0.15 - fi * 0.14
          + sin(uv.x * (1.6 + fi * 0.35) + t * (0.6 + fi * 0.15) + fi * 1.7) * 0.06
          + snoise(vec2(uv.x * 0.8 + fi * 3.0, t * 0.3)) * 0.04;
        float body = 1.0 - smoothstep(-0.004, 0.004, uv.y - y);
        float edge = exp(-abs(uv.y - y) * 60.0);
        vec3 tint = mix(u_glow, u_accent, fi / 4.0);
        col = mix(col, mix(u_base, tint, 0.35 * u_glowAmt + 0.05), body * 0.35);
        col += tint * edge * 0.10 * u_intensity * mix(1.0, 0.4, u_light);
      }
      col += grain() * 0.012;
      gl_FragColor = vec4(col, 1.0);
    }`,

  // No animation: the ground with a soft vertical gradient (touch effects still apply).
  plain: `
    void main() {
      vec2 uv = centeredUv();
      vec3 col = mix(u_base, u_mid, (1.0 - smoothstep(-0.6, 1.2, uv.y)) * 0.5);
      col += grain() * 0.008;
      gl_FragColor = vec4(col, 1.0);
    }`,
};

export const ShaderBG: React.FC<ShaderBGProps> = ({
  intensity = 1.3,
  style,
  mode = 'nebula',
  palette = SKINS.noir.background,
  motion = 1,
  density = 1,
  interactive = true,
}) => {
  const ref = useRef<HTMLCanvasElement>(null);
  const [fallback, setFallback] = React.useState(false);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // 'plain' has nothing to animate: it only redraws for touch effects
    const still = prefersReduced || motion <= 0 || mode === 'plain';
    const touch = interactive && !prefersReduced;
    const gl = canvas.getContext('webgl', { premultipliedAlpha: false, alpha: true, antialias: false });
    if (!gl) {
      setFallback(true);
      return;
    }
    const needsDerivatives = mode === 'silk' || mode === 'grid';
    if (needsDerivatives) gl.getExtension('OES_standard_derivatives');

    const vsrc = `attribute vec2 a_pos; void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }`;
    const ext = needsDerivatives ? '#extension GL_OES_standard_derivatives : enable\n' : '';
    const fsrc = ext + HEADER + MODES[mode].replace(/gl_FragColor = vec4\(col, 1\.0\);/g, 'gl_FragColor = vec4(col + touchGlow(), 1.0);');

    function compile(type: number, src: string) {
      const s = gl!.createShader(type);
      if (!s) return null;
      gl!.shaderSource(s, src);
      gl!.compileShader(s);
      if (!gl!.getShaderParameter(s, gl!.COMPILE_STATUS)) {
        console.error('[ShaderBG]', mode, gl!.getShaderInfoLog(s));
        gl!.deleteShader(s);
        return null;
      }
      return s;
    }

    const vs = compile(gl.VERTEX_SHADER, vsrc);
    const fs = compile(gl.FRAGMENT_SHADER, fsrc);
    const prog = vs && fs ? gl.createProgram() : null;
    if (!vs || !fs || !prog) {
      setFallback(true);
      return;
    }
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    gl.useProgram(prog);

    const quad = new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, quad, gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(prog, 'a_pos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    const u = (n: string) => gl.getUniformLocation(prog, n);
    const uRes = u('u_res');
    const uTime = u('u_time');
    const uPointer = u('u_pointer');
    const uTap = u('u_tap');
    gl.uniform1f(u('u_intensity'), intensity);
    gl.uniform3fv(u('u_base'), palette.base);
    gl.uniform3fv(u('u_mid'), palette.mid);
    gl.uniform3fv(u('u_glow'), palette.glow);
    gl.uniform3fv(u('u_accent'), palette.accent);
    gl.uniform1f(u('u_glowAmt'), palette.glowAmount);
    gl.uniform1f(u('u_density'), density);
    // perceived lightness of the ground: 1 on light skins
    gl.uniform1f(u('u_light'), 0.2126 * palette.base[0] + 0.7152 * palette.base[1] + 0.0722 * palette.base[2] > 0.5 ? 1 : 0);

    // Touch state, in centeredUv units. Strength eases towards its target; taps age in seconds.
    const pointer = { x: 0, y: 0, strength: 0, target: 0 };
    const tap = { x: 0, y: 0, at: -1e9 };
    const TAP_LIFE = 2.2;

    let raf = 0;
    let running = false;
    const t0 = performance.now();
    const draw = () => {
      const now = performance.now();
      gl.uniform2f(uRes, canvas.width, canvas.height);
      // Still frame at a pleasant point of the animation
      gl.uniform1f(uTime, still ? 12.0 : ((now - t0) / 1000) * motion);
      gl.uniform3f(uPointer, pointer.x, pointer.y, pointer.strength);
      gl.uniform3f(uTap, tap.x, tap.y, (now - tap.at) / 1000);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    };
    // Animated modes always loop; still ones only while a touch effect is fading
    const busy = () => !still || pointer.strength > 0.002 || pointer.target > 0 || (performance.now() - tap.at) / 1000 < TAP_LIFE;
    const tick = () => {
      pointer.strength += (pointer.target - pointer.strength) * 0.08;
      draw();
      if (busy() && !document.hidden) raf = requestAnimationFrame(tick);
      else running = false;
    };
    const kick = () => {
      if (running || document.hidden) return;
      running = true;
      raf = requestAnimationFrame(tick);
    };

    function resize() {
      if (!canvas) return;
      // 1.5x is visually identical behind blurred UI and much cheaper on phones
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = Math.max(1, Math.round(canvas.clientWidth * dpr));
      canvas.height = Math.max(1, Math.round(canvas.clientHeight * dpr));
      gl!.viewport(0, 0, canvas.width, canvas.height);
      draw();
    }
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    // The canvas sits under the UI (pointer-events: none), so listen on the window
    const toUv = (e: PointerEvent) => {
      const r = canvas.getBoundingClientRect();
      const m = Math.min(r.width, r.height) || 1;
      return { x: (e.clientX - r.left - r.width / 2) / m, y: (r.top + r.height / 2 - e.clientY) / m, inside: e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom };
    };
    const onMove = (e: PointerEvent) => {
      const p = toUv(e);
      pointer.x = p.x; pointer.y = p.y;
      // Mouse: glow while hovering; touch: only while the finger is down
      pointer.target = p.inside && (e.pointerType === 'mouse' || e.buttons > 0 || e.pressure > 0) ? 1 : 0;
      kick();
    };
    const onDown = (e: PointerEvent) => {
      const p = toUv(e);
      if (!p.inside) return;
      pointer.x = p.x; pointer.y = p.y; pointer.target = 1;
      tap.x = p.x; tap.y = p.y; tap.at = performance.now();
      kick();
    };
    const onUp = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') pointer.target = 0;
      kick();
    };
    const onLeave = () => { pointer.target = 0; kick(); };
    if (touch) {
      window.addEventListener('pointermove', onMove, { passive: true });
      window.addEventListener('pointerdown', onDown, { passive: true });
      window.addEventListener('pointerup', onUp, { passive: true });
      window.addEventListener('pointercancel', onUp, { passive: true });
      document.documentElement.addEventListener('pointerleave', onLeave);
    }
    const onVisibility = () => {
      cancelAnimationFrame(raf);
      running = false;
      if (!document.hidden) kick();
    };
    document.addEventListener('visibilitychange', onVisibility);
    kick();

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('visibilitychange', onVisibility);
      if (touch) {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerdown', onDown);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        document.documentElement.removeEventListener('pointerleave', onLeave);
      }
      ro.disconnect();
      gl.deleteBuffer(buf);
      gl.deleteProgram(prog);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
    };
  }, [intensity, mode, palette, motion, density, interactive]);

  if (fallback) {
    return <div className="no-shader-bg" style={style} />;
  }

  return <canvas ref={ref} aria-hidden="true" className="absolute inset-0 w-full h-full block pointer-events-none z-0" style={style} data-bg={mode} />;
};

export default ShaderBG;
